import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ParcelType } from "@/hooks/useParcelTypes";
import { getIconByName, getAvailableIcons } from "@/lib/icon-utils";

interface FormData {
  key: string;
  title: string;
  description: string;
  icon_name: string;
  color_from: string;
  color_to: string;
  is_enabled: boolean;
  default_weight: string;
  default_dimensions: {
    length?: string;
    width?: string;
    height?: string;
  };
}

interface ParcelTypeFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingType: ParcelType | null;
  onSubmit: (data: FormData) => Promise<void>;
  parcelTypesCount: number;
}

const ParcelTypeForm = ({ open, onOpenChange, editingType, onSubmit, parcelTypesCount }: ParcelTypeFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    key: '',
    title: '',
    description: '',
    icon_name: 'Package',
    color_from: 'from-blue-500',
    color_to: 'to-blue-600',
    is_enabled: true,
    default_weight: '',
    default_dimensions: {
      length: '',
      width: '',
      height: ''
    }
  });

  const resetForm = () => {
    setFormData({
      key: '',
      title: '',
      description: '',
      icon_name: 'Package',
      color_from: 'from-blue-500',
      color_to: 'to-blue-600',
      is_enabled: true,
      default_weight: '',
      default_dimensions: {
        length: '',
        width: '',
        height: ''
      }
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.key || !formData.title || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const dataToSubmit = {
        ...formData,
        default_weight: formData.default_weight || null,
        default_dimensions: (formData.default_dimensions.length || formData.default_dimensions.width || formData.default_dimensions.height) 
          ? formData.default_dimensions 
          : null,
        sort_order: editingType ? editingType.sort_order : parcelTypesCount + 1
      };

      await onSubmit(dataToSubmit);
      onOpenChange(false);
      resetForm();
    } catch (error) {
      console.error('Error saving parcel type:', error);
    }
  };

  // Populate form when editing
  React.useEffect(() => {
    if (editingType && open) {
      setFormData({
        key: editingType.key,
        title: editingType.title,
        description: editingType.description,
        icon_name: editingType.icon_name,
        color_from: editingType.color_from,
        color_to: editingType.color_to,
        is_enabled: editingType.is_enabled,
        default_weight: editingType.default_weight || '',
        default_dimensions: editingType.default_dimensions || {
          length: '',
          width: '',
          height: ''
        }
      });
    }
  }, [editingType, open]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editingType ? 'Edit Parcel Type' : 'Add New Parcel Type'}
          </DialogTitle>
          <DialogDescription>
            Configure the parcel type details and appearance
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="key">Key *</Label>
              <Input
                id="key"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                placeholder="e.g., documents"
                disabled={!!editingType}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Documents & Paperwork"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Letters, contracts, certificates"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="icon_name">Icon</Label>
              <Select value={formData.icon_name} onValueChange={(value) => setFormData({ ...formData, icon_name: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableIcons().map((iconName) => {
                    const IconComponent = getIconByName(iconName);
                    return (
                      <SelectItem key={iconName} value={iconName}>
                        <div className="flex items-center gap-2">
                          <IconComponent className="h-4 w-4" />
                          {iconName}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="color_from">From Color</Label>
              <Input
                id="color_from"
                value={formData.color_from}
                onChange={(e) => setFormData({ ...formData, color_from: e.target.value })}
                placeholder="from-blue-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="color_to">To Color</Label>
              <Input
                id="color_to"
                value={formData.color_to}
                onChange={(e) => setFormData({ ...formData, color_to: e.target.value })}
                placeholder="to-blue-600"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="default_weight">Default Weight (kg)</Label>
            <Input
              id="default_weight"
              value={formData.default_weight}
              onChange={(e) => setFormData({ ...formData, default_weight: e.target.value })}
              placeholder="e.g., 0.5"
            />
          </div>

          <div className="space-y-2">
            <Label>Default Dimensions (cm)</Label>
            <div className="grid grid-cols-3 gap-2">
              <Input
                value={formData.default_dimensions.length}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  default_dimensions: { 
                    ...formData.default_dimensions, 
                    length: e.target.value 
                  }
                })}
                placeholder="Length"
              />
              <Input
                value={formData.default_dimensions.width}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  default_dimensions: { 
                    ...formData.default_dimensions, 
                    width: e.target.value 
                  }
                })}
                placeholder="Width"
              />
              <Input
                value={formData.default_dimensions.height}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  default_dimensions: { 
                    ...formData.default_dimensions, 
                    height: e.target.value 
                  }
                })}
                placeholder="Height"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_enabled"
              checked={formData.is_enabled}
              onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
            />
            <Label htmlFor="is_enabled">Enabled</Label>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {editingType ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ParcelTypeForm;
